import { SpeedInsights } from "@vercel/speed-insights/react";
import { Analytics } from "@vercel/analytics/react";
import React, { useState, useEffect, useCallback } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Link,
  useParams,
  useNavigate,
} from "react-router-dom";

import { GAMES_DATA } from "./games_data";

const GameSelector = ({ onSelectGame }) => (
  <div className="flex flex-col items-center justify-center h-full">
    <h2 className="text-2xl font-bold mb-5">Select a Game</h2>
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
      {Object.keys(GAMES_DATA).map((gameKey) => (
        <button
          key={gameKey}
          onClick={() => onSelectGame(gameKey)}
          className="px-5 py-3 rounded text-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors duration-200"
        >
          {GAMES_DATA[gameKey].title}
        </button>
      ))}
    </div>
  </div>
);

const LinkedPuzzleDemo = () => {
  const [currentGame, setCurrentGame] = useState(null);
  const [gameStartTime, setGameStartTime] = useState(null);
  const [penaltyTime, setPenaltyTime] = useState(0);
  const [displayedTime, setDisplayedTime] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);
  const [message, setMessage] = useState("");
  const [messageVisible, setMessageVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [targetTime, setTargetTime] = useState(0);
  const [isPuzzleComplete, setIsPuzzleComplete] = useState(false);
  const [correctBoxes, setCorrectBoxes] = useState([]);
  const [penaltyAmount, setPenaltyAmount] = useState(10); // Default penalty amount
  const [isPenaltyAnimating, setIsPenaltyAnimating] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const { gameId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (gameId && GAMES_DATA[gameId]) {
      selectGame(gameId);
    }
  }, [gameId]);

  const goToHome = () => {
    setCurrentGame(null);
    setSelectedItems([]);
    setMessage("");
    setMessageVisible(false);
    setTimeLeft(0);
    setIsCountingDown(false);
    setTargetTime(0);
    setIsPuzzleComplete(false);
    setGameStartTime(null);
    navigate("/");
  };

  useEffect(() => {
    let timer;
    if (currentGame && !isPuzzleComplete && gameStartTime && !isPenaltyAnimating) {
      timer = setInterval(() => {
        const elapsedTime = Math.floor((Date.now() - gameStartTime) / 1000) + penaltyTime;
        const newTimeLeft = Math.max(currentGame.timeLimit - elapsedTime, 0);

        setTimeLeft(newTimeLeft);
        setDisplayedTime(newTimeLeft);

        if (newTimeLeft === 0) {
          clearInterval(timer);
          setMessage("Sorry, you didn't solve the puzzle. Time's up!");
          setMessageVisible(true);
          setIsPuzzleComplete(true);
        }
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [currentGame, isPuzzleComplete, gameStartTime, penaltyTime, isPenaltyAnimating]);

  const selectGame = (gameKey) => {
    const game = GAMES_DATA[gameKey];
    let initialItems;
    let isValidInitialPath;

    const isValidPath = (path) => {
      return path.every((item, index, arr) => {
        if (index === arr.length - 1) return true;
        const link = `${item}${arr[index + 1]}`;
        return game.links.includes(link);
      });
    };

    do {
      initialItems = game.columns.map((column) => Math.floor(Math.random() * column.length));
      const initialPath = ["<", ...initialItems.map((item, index) => game.columns[index][item].letter), ">"];
      isValidInitialPath = isValidPath(initialPath);
    } while (isValidInitialPath);

    setCurrentGame(game);
    setSelectedItems(initialItems);
    setTimeLeft(game.timeLimit);
    setDisplayedTime(game.timeLimit);
    setTargetTime(game.timeLimit);
    setMessage("");
    setMessageVisible(false);
    setIsCountingDown(false);
    setIsPuzzleComplete(false);
    setGameStartTime(Date.now());
    setPenaltyTime(0);
    setCorrectBoxes([]);
    setPenaltyAmount(game.penaltyAmount || 10); // Set penalty amount, default to 10 if not specified
    navigate(`/game/${gameKey}`);
  };

  useEffect(() => {
    let animationTimer;
    if (isCountingDown) {
      const startTime = displayedTime;
      const endTime = timeLeft;
      const duration = 1000; // 1 second animation
      const startTimestamp = performance.now();

      const animate = (currentTimestamp) => {
        const elapsed = currentTimestamp - startTimestamp;
        if (elapsed < duration) {
          const timeValue = Math.ceil(
            startTime + (endTime - startTime) * (elapsed / duration)
          );
          setDisplayedTime(timeValue);
          animationTimer = requestAnimationFrame(animate);
        } else {
          setDisplayedTime(endTime);
          setIsCountingDown(false);
        }
      };

      animationTimer = requestAnimationFrame(animate);
    } else {
      setDisplayedTime(timeLeft);
    }

    return () => {
      if (animationTimer) {
        cancelAnimationFrame(animationTimer);
      }
    };
  }, [isCountingDown, timeLeft]);

  const applyPenalty = useCallback(() => {
    setIsPenaltyAnimating(true);
    setIsButtonDisabled(true);
    const startTime = timeLeft;
    const endTime = Math.max(timeLeft - penaltyAmount, 0);
    const animationDuration = 500; // 0.5 second animation
    const startTimestamp = performance.now();

    const animatePenalty = (currentTimestamp) => {
      const elapsed = currentTimestamp - startTimestamp;
      if (elapsed < animationDuration) {
        const timeValue = Math.ceil(
          startTime + (endTime - startTime) * (elapsed / animationDuration)
        );
        setDisplayedTime(timeValue);
        requestAnimationFrame(animatePenalty);
      } else {
        setDisplayedTime(endTime);
        setTimeLeft(endTime);
        setPenaltyTime((prevPenalty) => prevPenalty + penaltyAmount);
        setIsPenaltyAnimating(false);
        setIsButtonDisabled(false);

        if (endTime === 0) {
          setMessage("Sorry, you didn't solve the puzzle. Time's up!");
          setMessageVisible(true);
          setIsPuzzleComplete(true);
        }
      }
    };

    requestAnimationFrame(animatePenalty);
  }, [timeLeft, penaltyAmount]);

  const moveColumn = (columnIndex, direction) => {
    if (isPuzzleComplete) return;
    setSelectedItems((prevItems) => {
      const newItems = [...prevItems];
      if (direction === "up") {
        newItems[columnIndex] =
          (newItems[columnIndex] -
            1 +
            currentGame.columns[columnIndex].length) %
          currentGame.columns[columnIndex].length;
      } else {
        newItems[columnIndex] =
          (newItems[columnIndex] + 1) % currentGame.columns[columnIndex].length;
      }
      return newItems;
    });
  };

  const isValidPath = (path) => {
    if (!currentGame) return false;
    return path.every((item, index, arr) => {
      if (index === arr.length - 1) return true;
      const link = `${item}${arr[index + 1]}`;
      return currentGame.links.includes(link);
    });
  };

  const checkSolution = () => {
    if (!currentGame || isPuzzleComplete) return;

    const selectedPath = [
      "<",
      ...selectedItems.map(
        (item, index) => currentGame.columns[index][item].letter
      ),
      ">",
    ];

    console.log("Selected items:", selectedItems);
    console.log("Selected path:", selectedPath);

    const isValid = isValidPath(selectedPath);
    const isComplete = selectedPath.length === currentGame.columns.length + 2;

    console.log("Is valid path:", isValid);
    console.log("Is complete path:", isComplete);

    if (isValid && isComplete) {
      setMessage("Correct! You've solved the puzzle!");
      setIsPuzzleComplete(true);
      animateCorrectSolution();
    } else {
      setMessage("Not quite right. Try again!");
      applyPenalty();
    }
    setMessageVisible(true);
  };

  const animateCorrectSolution = () => {
    const totalBoxes = currentGame.columns.length;
    let boxIndex = -1;

    const animateBox = () => {
      if (boxIndex <= totalBoxes) {
        setCorrectBoxes((prev) => [...prev, boxIndex]);
        boxIndex++;
        setTimeout(animateBox, 200);
      }
    };

    animateBox();
  };

  const renderChevronButton = (direction, onClick, disabled) => (
    <button
      onClick={onClick}
      disabled={disabled || isPuzzleComplete}
      className={`
        w-8 h-8 rounded-full flex items-center justify-center
        ${disabled || isPuzzleComplete
          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
          : "bg-blue-500 text-white hover:bg-yellow-500 hover:text-blue-800"
        }
        transition-colors duration-200
      `}
    >
      {direction === "up" ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
    </button>
  );

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")}`;
  };

  const renderFactBox = (fact, isSelected, columnIndex, isDisabled) => (
    <div
      className={`
        w-full flex items-center justify-center text-center
        ${isSelected && correctBoxes.includes(columnIndex)
          ? "bg-yellow-500 text-blue-800"
          : isSelected
            ? "bg-blue-500 text-white"
            : isDisabled
              ? "bg-gray-300 text-gray-500"
              : "bg-blue-200 text-blue-800"
        }
        ${isSelected ? "h-20 z-10" : "h-20 transform scale-90"}
        transition-all duration-300 ease-in-out rounded
      `}
    >
      <p
        className={`font-bold leading-tight ${isSelected ? "text-base" : "text-sm"
          }`}
      >
        {fact.text}
      </p>
    </div>
  );

  const renderGameBoard = () => (
    <div className="bg-white rounded-lg shadow-lg p-5">
      <div className="flex items-center mb-4 relative">
        <p className="text-center text-3xl w-full">
          Link{" "}
          <span className="font-bold text-yellow-500">
            {currentGame.startWord}
          </span>{" "}
          to{" "}
          <span className="font-bold text-yellow-500">{currentGame.endWord}</span>
          {" "}before the timer runs out!
        </p>
      </div>
      <div className="flex justify-center mb-6">
        <div
          className={`px-6 py-4 rounded text-3xl font-bold transition-colors duration-300 w-36 text-center
          ${isPuzzleComplete && message.includes("Correct")
              ? "bg-green-500 text-white"
              : timeLeft === 0 || (isPuzzleComplete && !message.includes("Correct"))
                ? "bg-red-600 text-white"
                : isCountingDown || isPenaltyAnimating
                  ? "bg-red-600 text-white"
                  : "bg-gray-500 text-white"
            }`}
        >
          {formatTime(Math.ceil(displayedTime))}
        </div>
      </div>

      <div className="flex flex-wrap justify-center items-center mb-10">
        <div className="flex flex-col items-center justify-center w-32 m-1">
          <div className="bg-yellow-500 p-2 text-center w-full h-20 flex items-center justify-center font-bold rounded-lg">
            <p className="text-base leading-tight">{currentGame.startWord}</p>
          </div>
        </div>
        {currentGame.columns.map((column, columnIndex) => (
          <div
            key={columnIndex}
            className="flex flex-col items-center w-32 m-1"
          >
            {renderChevronButton(
              "up",
              () => moveColumn(columnIndex, "up"),
              isPuzzleComplete
            )}
            <div className="w-full h-64 flex flex-col justify-center space-y-1 my-2">
              {[-1, 0, 1].map((offset) => {
                const index =
                  (selectedItems[columnIndex] + offset + column.length) %
                  column.length;
                return (
                  <div
                    key={offset}
                    className="w-full h-20 flex items-center justify-center"
                  >
                    {renderFactBox(column[index], offset === 0, columnIndex, isPuzzleComplete && offset !== 0)}
                  </div>
                );
              })}
            </div>
            {renderChevronButton(
              "down",
              () => moveColumn(columnIndex, "down"),
              isPuzzleComplete
            )}
          </div>
        ))}
        <div className="flex flex-col items-center justify-center w-32 m-1">
          <div className="bg-yellow-500 p-2 text-center w-full h-20 flex items-center justify-center font-bold rounded-lg">
            <p className="text-base leading-tight">{currentGame.endWord}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center mt-5">
        <button
          onClick={checkSolution}
          className={`bg-yellow-500 text-blue-800 px-8 py-3 rounded-full text-xl font-bold mb-4
            ${isPuzzleComplete || isButtonDisabled
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-yellow-400"
            }`}
          disabled={isPuzzleComplete || isButtonDisabled}
        >
          LINKED?
        </button>
        <div className="h-10 mt-5">
          {messageVisible && (
            <p
              className={`text-xl font-semibold text-center transition-opacity duration-500 ${messageVisible ? "opacity-100" : "opacity-0"
                }`}
            >
              {message}
            </p>
          )}
        </div>
        <div className="w-full flex justify-start">
          <button
            onClick={goToHome}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors duration-200"
          >
            More Puzzles
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#e6e6e6] flex justify-center items-center">
      <div className="w-full max-w-7xl p-5">
        {currentGame ? (
          renderGameBoard()
        ) : (
          <>
            <h1 className="text-4xl font-bold mb-5 text-left text-blue-600 hover:text-blue-800 transition-colors duration-200 cursor-pointer">
              Linked Puzzle Games
            </h1>
            <GameSelector onSelectGame={selectGame} />
          </>
        )}
      </div>
      <SpeedInsights />
      <Analytics />
    </div>
  );
};

const App = () => (
  <Router>
    <Routes>
      <Route path="/" element={<LinkedPuzzleDemo />} />
      <Route path="/game/:gameId" element={<LinkedPuzzleDemo />} />
    </Routes>
  </Router>
);

export default App;